import { useEffect, useRef, useState } from 'react';
import { Animated, type LayoutChangeEvent, Pressable } from 'react-native';
import { useTranslation } from '../contexts/TranslationContext';
import { useLanguageSync } from '../features/settings/hooks/useLanguageSync';
import type { Language } from '../i18n/translations';
import { trackEvent } from '../lib/analytics';
import { useMixpanel } from '../lib/mixpanel';
import { colors } from '../theme/colors';
import { withAlpha } from '../theme/colorUtils';
import { Box } from './primitives/Box';
import { Text } from './primitives/Text';

interface LanguageSwitcherProps {
  condensed?: boolean;
}

export function LanguageSwitcher({ condensed = false }: Readonly<LanguageSwitcherProps>) {
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguageSync();
  const mp = useMixpanel();

  const handleLanguageChange = (code: Language) => {
    if (code === language) return;
    trackEvent(mp, 'landing.language_changed', { from: language, to: code, surface: 'app' });
    mp.register({ locale: code });
    changeLanguage(code);
  };
  const [layouts, setLayouts] = useState<Record<Language, { x: number; width: number }>>({
    en: { x: 0, width: 0 },
    es: { x: 0, width: 0 },
    pt: { x: 0, width: 0 },
  });
  const indicatorX = useRef(new Animated.Value(0)).current;
  const indicatorWidth = useRef(new Animated.Value(0)).current;
  const textOpacity = useRef(new Animated.Value(1)).current;
  const isFirstRender = useRef(true);

  const options: { code: Language; label: string; name: string }[] = [
    { code: 'en', label: t('common.englishShort'), name: t('common.english') },
    { code: 'es', label: t('common.spanishShort'), name: t('common.spanish') },
    { code: 'pt', label: t('common.portugueseShort'), name: t('common.portuguese') },
  ];

  useEffect(() => {
    const layout = layouts[language];
    if (!layout || layout.width === 0) return;

    if (isFirstRender.current) {
      indicatorX.setValue(layout.x);
      indicatorWidth.setValue(layout.width);
      isFirstRender.current = false;
    } else {
      Animated.parallel([
        Animated.spring(indicatorX, {
          toValue: layout.x,
          useNativeDriver: false,
          bounciness: 8,
          speed: 20,
        }),
        Animated.timing(indicatorWidth, {
          toValue: layout.width,
          duration: 180,
          useNativeDriver: false,
        }),
        Animated.sequence([
          Animated.timing(textOpacity, { toValue: 0.4, duration: 100, useNativeDriver: true }),
          Animated.timing(textOpacity, { toValue: 1, duration: 140, useNativeDriver: true }),
        ]),
      ]).start();
    }
  }, [language, indicatorWidth, indicatorX, textOpacity, layouts]);

  const handleLayout = (code: Language) => (event: LayoutChangeEvent) => {
    const { x, width } = event.nativeEvent.layout;
    setLayouts((prev) => ({ ...prev, [code]: { x, width } }));
  };

  return (
    <Box flexDirection="row" alignItems="center" gap="sm">
      {!condensed && (
        <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '600' }}>
          {t('common.languageLabel')}
        </Text>
      )}
      <Box
        flexDirection="row"
        backgroundColor={colors.background.card}
        borderRadius="full"
        borderWidth={1}
        borderColor={withAlpha(colors.primary[100], 0.15)}
        overflow="hidden"
        position="relative"
      >
        <Animated.View
          pointerEvents="none"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            borderRadius: 999,
            backgroundColor: colors.primary[500],
            transform: [{ translateX: indicatorX }],
            width: indicatorWidth,
          }}
        />
        {options.map((option) => {
          const isActive = language === option.code;
          return (
            <Pressable
              key={option.code}
              onPress={() => handleLanguageChange(option.code)}
              onLayout={handleLayout(option.code)}
            >
              <Box paddingX="md" paddingY="xs" backgroundColor="transparent">
                <Animated.Text
                  style={{
                    opacity: textOpacity,
                    fontWeight: '700',
                    letterSpacing: 0.5,
                    color: isActive ? colors.background.primary : colors.text.secondary,
                    fontSize: 12,
                  }}
                >
                  {condensed ? option.label : option.name}
                </Animated.Text>
              </Box>
            </Pressable>
          );
        })}
      </Box>
    </Box>
  );
}
